import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { IL_COLORS } from "../../../../constants/investmentLeague";
import SummaryMetrics from "./SummaryMetrics";
import PortfolioValueTrend from "./PortfolioValueTrend";
import PerformanceCallouts from "./PerformanceCallouts";
import HoldingsTable from "./HoldingsTable";
import TabLoader from "../TabLoader";
import SectionLoader from "../SectionLoader";

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const InvestmentSummary = ({
  data,
  loading,
  error,
  showMacSelector = false,
  macOptions = [],
  selectedMac,
  onMacChange,
  selectedQuarter: controlledSelectedQuarter,
  onQuarterChange,
}) => {
  const theme = useTheme();
  const t     = theme.custom;
  const [search, setSearch] = useState("");
  const [internalQuarter, setInternalQuarter] = useState(null);

  const availableQuarters = useMemo(() => {
    if (data?.quarters && data.quarters.length > 0) return data.quarters;
    if (data?.allHoldings && data.allHoldings.length > 0) {
      const quarters = Array.from(new Set(data.allHoldings.map((h) => h.quarter).filter(Boolean)));
      if (quarters.length > 0) return quarters;
    }
    return ["Q1", "Q2"];
  }, [data]);

  const currentQuarterSetting = controlledSelectedQuarter !== undefined ? controlledSelectedQuarter : internalQuarter;

  const activeQuarter = useMemo(() => {
    if (currentQuarterSetting && availableQuarters.includes(currentQuarterSetting)) {
      return currentQuarterSetting;
    }
    return data?.latestQuarter || availableQuarters[availableQuarters.length - 1] || "Q1";
  }, [currentQuarterSetting, availableQuarters, data?.latestQuarter]);

  const handleQuarterChange = (newQuarter) => {
    if (onQuarterChange) {
      onQuarterChange(newQuarter);
    }
    setInternalQuarter(newQuarter);
  };

  const quarterData = useMemo(() => {
    if (data?.byQuarter?.[activeQuarter]) {
      return data.byQuarter[activeQuarter];
    }
    const sourceHoldings = data?.allHoldings ?? data?.holdings ?? [];
    const holdings = sourceHoldings.filter(
      (h) => (h.quarter || "Q1") === activeQuarter
    );
    const invested = holdings.reduce((sum, h) => sum + (Number(h.invested) || 0), 0);
    const current = holdings.reduce((sum, h) => sum + (Number(h.current) || 0), 0);
    const returns = holdings.reduce((sum, h) => sum + (Number(h.returns) || 0), 0);
    const returnsPct = invested > 0 ? (returns / invested) * 100 : 0;
    const totals = { invested, current, returns, returnsPct, count: holdings.length };

    return {
      quarter: activeQuarter,
      holdings,
      totals,
      metrics: data?.metrics || [],
      trendData: data?.trendData || [],
    };
  }, [data, activeQuarter]);

  const holdings = quarterData?.holdings ?? [];
  const metrics = quarterData?.metrics ?? data?.metrics ?? [];
  const trendData = quarterData?.trendData ?? data?.trendData ?? [];
  const totals = quarterData?.totals ?? data?.totals;

  const sortedHoldings = useMemo(
    () => [...holdings].sort((a, b) => b.returnsPct - a.returnsPct),
    [holdings]
  );

  const bestPerformer = useMemo(() => {
    if (!sortedHoldings.length) return null;
    return sortedHoldings.reduce(
      (best, h) => (h.returnsPct > best.returnsPct ? h : best),
      sortedHoldings[0]
    );
  }, [sortedHoldings]);

  const filteredHoldings = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedHoldings;
    return sortedHoldings.filter(
      (h) =>
        h.ticker.toLowerCase().includes(q) ||
        h.name.toLowerCase().includes(q) ||
        h.buUnit?.toLowerCase().includes(q)
    );
  }, [sortedHoldings, search]);

  if (loading && !data) {
    return <TabLoader message="Loading investment summary..." />;
  }

  if (error && !data) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography sx={{ fontSize: "14px", color: t.text2 }}>{error}</Typography>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography sx={{ fontSize: "14px", color: t.text3 }}>No investment data available.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "1280px", mx: "auto", position: "relative" }}>
      {loading && (
        <Box
          sx={{
            position:        "absolute",
            inset:           0,
            zIndex:          2,
            display:         "flex",
            alignItems:      "center",
            justifyContent:  "center",
            background:      "rgba(0,0,0,0.25)",
            borderRadius:    "10px",
          }}
        >
          <SectionLoader minHeight={80} />
        </Box>
      )}
      <Box
        sx={{
          display:        "flex",
          flexDirection:  { xs: "column", md: "row" },
          alignItems:     { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap:            2,
          mb:             3,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:   { xs: "22px", md: "28px" },
              fontWeight: 700,
              color:      t.text,
              mb:         0.5,
            }}
          >
            Investment Summary
          </Typography>
          <Typography sx={{ fontSize: "13px", color: t.text3 }}>
            Your portfolio performance across the PSIOG Capital Growth League
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          {showMacSelector && macOptions.length > 0 && (
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: "100%", sm: 240 },
                "& .MuiInputBase-root": { height: "auto" },
              }}
            >
              <Select
                value={selectedMac ?? ""}
                onChange={(e) => onMacChange?.(e.target.value)}
                displayEmpty
                sx={{
                  borderRadius: "16px",
                  fontSize:     "11px",
                  fontWeight:   600,
                  background:   t.surface2,
                  height:       "auto",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: t.border2,
                  },
                  "& .MuiSelect-select": {
                    py:        0.5,
                    px:        1.5,
                    minHeight: "unset !important",
                    lineHeight: 1.4,
                  },
                }}
              >
                {macOptions.map((mac) => (
                  <MenuItem key={mac.code} value={mac.name}>
                    {mac.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {/* <Box
            sx={{
              px:           1.5,
              py:           0.5,
              borderRadius: "16px",
              border:       `1px solid ${t.border2}`,
              background:   t.surface,
              fontSize:     "11px",
              fontWeight:   500,
              color:        t.text2,
            }}
          >
            2022-23
          </Box> */}
          {availableQuarters.length > 0 && (
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: "100%", sm: 90 },
                "& .MuiInputBase-root": { height: "auto" },
              }}
            >
              <Select
                value={activeQuarter ?? ""}
                onChange={(e) => handleQuarterChange(e.target.value)}
                displayEmpty
                sx={{
                  borderRadius: "16px",
                  fontSize:     "11px",
                  fontWeight:   600,
                  background:   t.surface2,
                  height:       "auto",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: t.border2,
                  },
                  "& .MuiSelect-select": {
                    py:        0.5,
                    px:        1.5,
                    minHeight: "unset !important",
                    lineHeight: 1.4,
                  },
                }}
              >
                {availableQuarters.map((q) => (
                  <MenuItem key={q} value={q}>
                    {q}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Box
            sx={{
              px:           1.5,
              py:           0.5,
              borderRadius: "16px",
              background:   "rgba(45,138,91,0.12)",
              border:       `1px solid rgba(45,138,91,0.25)`,
              fontSize:     "11px",
              fontWeight:   600,
              color:        IL_COLORS.positive,
            }}
          >
            FY 2026-27
          </Box>
        </Box>
      </Box>

      <SummaryMetrics metrics={metrics} />

      <Box
        sx={{
          display:             "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.4fr 1fr" },
          gap:                 2,
          mb:                  3,
        }}
      >
        <PortfolioValueTrend data={trendData} />
        {bestPerformer && (
          <PerformanceCallouts best={bestPerformer} />
        )}
      </Box>

      <Box
        sx={{
          background:   t.surface,
          border:       `1px solid ${t.border2}`,
          borderRadius: "10px",
          overflow:     "hidden",
        }}
      >
        <Box
          sx={{
            display:        "flex",
            flexDirection:  { xs: "column", sm: "row" },
            alignItems:     { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap:            2,
            px:             { xs: 2, md: 3 },
            py:             2,
            borderBottom:   `1px solid ${t.border}`,
          }}
        >
          <Typography sx={{ fontSize: "15px", fontWeight: 600, color: t.text }}>
            Your Holdings ({sortedHoldings.length} positions)
          </Typography>

          <TextField
            size="small"
            placeholder="Search holdings..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ color: t.text3, display: "flex" }}>
                    <SearchIcon />
                  </Box>
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: "240px" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                fontSize:     "13px",
                background:   t.surface2,
              },
            }}
          />
        </Box>

        <HoldingsTable
          holdings={filteredHoldings}
          totals={totals}
          highlightBest={bestPerformer?.returnsPct > 0 ? bestPerformer?.buUnit : null}
        />
      </Box>

      <Typography
        sx={{
          textAlign: "center",
          fontSize:  "11px",
          color:     t.text3,
          mt:        3,
          mb:        1,
        }}
      >
        Values shown reflect of cash vs asset proceeds — use this view to inform your {activeQuarter} resilience
      </Typography>
    </Box>
  );
};

export default InvestmentSummary;
